import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import aiApiClient from '../../apiClient/ai-api-client';
import { genWorkspaceHeader } from '../../utils/constants';
import { message } from 'antd';
import { Notification } from '../../components/Notification';

// Enum for embedding statuses
const FILE_EMBEDDING_STATUS = {
  PENDING: 'PENDING',
  IN_PROGRESS: 'IN_PROGRESS',
  COMPLETED: 'COMPLETED',
  FAILED: 'FAILED',
};

// Initial state with file-specific tracking for embedding statuses, loading, and errors
const initialState = {
  loading: {
    summarizeFile: false,
  },
  embeddingStatus: {},
  error: {},
  summarizeFile: '',
};

// Helper function for error handling to reduce redundancy
const handleError = (err, rejectWithValue) => {
  const errorMessage = err?.data?.error?.message || err?.message || 'Something went wrong';
  return rejectWithValue({ error: errorMessage });
};

// Async thunk for fetching embedding status
export const fetchEmbeddingStatus = createAsyncThunk(
  'ai/fetchEmbeddingStatus',
  async (payload, { rejectWithValue }) => {
    const { fileInfo, workspaceInfo } = payload;
    try {
      const response = await aiApiClient.get(`/status`, {
        headers: genWorkspaceHeader(workspaceInfo?.id),
        params: { resource_id: fileInfo?.id },
      });
      return response;
    } catch (err) {
      return handleError(err, rejectWithValue);
    }
  }
);

// Async thunk for creating file embeddings
export const createFileEmbeddings = createAsyncThunk(
  'ai/createFileEmbeddings',
  async (payload, { rejectWithValue }) => {
    const { fileInfo, workspaceInfo } = payload;
    try {
      const response = await aiApiClient.post(
        '/ai/embeddings',
        {
          file_data: {
            id: fileInfo?.id,
            title: fileInfo?.title,
            markdown_data: fileInfo?.data,
            status: fileInfo?.status,
            file_owner: { name: fileInfo?.owner?.name, email: fileInfo?.owner?.email },
            workspace_id: workspaceInfo?.id,
            workspace_info: { title: workspaceInfo?.title },
            meta_info: {},
          },
        },
        { headers: genWorkspaceHeader(workspaceInfo?.id) }
      );
      return response;
    } catch (err) {
      message.error('Unable to create embeddings for file.');
      return handleError(err, rejectWithValue);
    }
  }
);

// Async thunk for summarize file
export const summarizeFile = createAsyncThunk('ai/summarizeFile', async (fileId, { rejectWithValue }) => {
  try {
    const response = await aiApiClient.get(`/ai/embeddings/summarize?file_id=${fileId}`);
    return response;
  } catch (err) {
    return handleError(err, rejectWithValue);
  }
});

// slice with the updated reducers and state tracking
const slice = createSlice({
  name: 'ai',
  initialState,
  extraReducers: (builder) => {
    // Fetch embedding status actions
    builder.addCase(fetchEmbeddingStatus.pending, (state, action) => {
      const { fileInfo } = action.meta.arg;
      state.loading[fileInfo?.id] = true;
    });
    builder.addCase(fetchEmbeddingStatus.fulfilled, (state, action) => {
      const { resource_id } = action.payload;
      state.loading[resource_id] = false;
      state.embeddingStatus[resource_id] = action?.payload;
    });
    builder.addCase(fetchEmbeddingStatus.rejected, (state, action) => {
      const { fileInfo } = action.meta.arg;
      state.loading[fileInfo?.id] = false;
      state.embeddingStatus[fileInfo?.id] = FILE_EMBEDDING_STATUS.FAILED;
      state.error[fileInfo?.id] = action.payload?.error || 'Failed to fetch embedding status';
    });

    // Create file embedding actions
    builder.addCase(createFileEmbeddings.pending, (state, action) => {
      const { fileInfo } = action.meta.arg;
      state.loading[fileInfo?.id] = true;
      state.embeddingStatus[fileInfo?.id] = FILE_EMBEDDING_STATUS.IN_PROGRESS;
    });
    builder.addCase(createFileEmbeddings.fulfilled, (state, action) => {
      const { resource_id } = action.payload;
      state.loading[resource_id] = false;
      state.embeddingStatus[resource_id] = action?.payload?.status;
    });
    builder.addCase(createFileEmbeddings.rejected, (state, action) => {
      const { fileInfo } = action.meta.arg;
      state.loading[fileInfo?.id] = false;
      state.embeddingStatus[fileInfo?.id] = FILE_EMBEDDING_STATUS.FAILED;
      state.error[fileInfo?.id] = action.payload?.error || 'Failed to create file embedding';
    });

    builder.addCase(summarizeFile.pending, (state, action) => {
      state.loading.summarizeFile = true;
    });
    builder.addCase(summarizeFile.fulfilled, (state, action) => {
      state.loading.summarizeFile = false;
      state.summarizeFile = action.payload?.response?.content?.content;
    });
    builder.addCase(summarizeFile.rejected, (state, action) => {
      state.loading.summarizeFile = false;
      Notification.error('Error happened while adding flock channels', action?.payload?.error);
    });
  },
});

export const selectFileEmbeddingStatus = (state, fileId) => {
  if (state.ai.embeddingStatus[fileId] !== undefined) {
    return state.ai.embeddingStatus[fileId];
  }
  return null;
};
export const selectFileError = (state, fileId) => state.ai.error[fileId];
export const selectFileLoading = (state, fileId) => {
  if (state.ai.loading[fileId] !== undefined) {
    return state.ai.loading[fileId];
  }
  return false;
};

export default slice.reducer;
